// Single source of truth for the coachee-web URL behind both chooser actions.
// "Open in the Sama app" and "Continue in browser" resolve to the SAME
// coachee-web URL (on coachee.sama.io — the domain the mobile apps claim for
// Universal / App Links) and differ only by a trailing control param, so both
// are built here to stop the two paths drifting.

// Matches the company-SSO route's pathname literally (two non-empty path
// segments after /coachee/auth/sso/). Gates the dynamic-route branch so stale
// routeParams from a different route can't compose a URL for a page the user
// isn't on.
const COMPANY_SSO_PATHNAME_PATTERN = /^\/coachee\/auth\/sso\/[^/]+\/[^/]+$/

// Per-route coachee-web URLs, sourced from env. Per environment these point at
// coachee.sama.io (prod) / staging-coachee.sama.io (staging) — the hosts that
// serve the AASA / assetlinks files and that the apps claim.
const coacheeWebUrlMap: Record<string, string | undefined> = {
    '/coachee/login': import.meta.env.VITE_APP_COACHEE_WEB_LOGIN_URL,
    '/coachee/verify-password': import.meta.env
        .VITE_APP_COACHEE_WEB_VERIFY_PASSWORD_URL,
    '/coachee/auth/okta/sso': import.meta.env
        .VITE_APP_COACHEE_WEB_OKTA_LOGIN_URL,
    '/coachee/auth/sama/sso': import.meta.env
        .VITE_APP_COACHEE_WEB_AUTH_PROVIDOR_LOGIN_URL,
    '/coachee/auth/sso/microsoft': import.meta.env
        .VITE_APP_COACHEE_WEB_MS_SSO_URL,
}

// Param-list derived from the backend's getLoginUrl in
// api/src/api/service/authentication/operations.authentication.service.ts.
// `state`/`authority_url`/`client_id` are MS-SSO-only; OAuth state validation
// depends on `state` surviving the bounce, and `authority_url` carries the
// full Microsoft authority (SAM-403) so the mobile apps can configure native
// MSAL for custom (per-company) authorities. `redirect_uri` is deliberately
// excluded — it's a coachee-web-internal value injected on the post-do_login
// re-entry, not part of email-link traffic. Control params (`do_login`,
// `app_attempt`) are added by the builders below, never copied from inbound.
export const ALLOWED_PARAMS = [
    'details',
    'phoneNumber',
    'magicToken',
    'state',
    'authority_url',
    'client_id',
    'lng',
] as const

export interface DeepLinkRouteParams {
    companyId?: string
    ssoId?: string
}

/**
 * Resolve the coachee-web URL for the current SPA route + inbound search
 * params, carrying the whitelisted params but WITHOUT a trailing control
 * param. Returns `''` when the route has no env mapping (misconfig / unmapped
 * path) so callers can fall back rather than build a broken URL.
 *
 * Magic-token landings always resolve to coachee-web's magic-login route,
 * regardless of which auth route the user originally hit.
 *
 * @param pathname - The SPA pathname (`window.location.pathname`).
 * @param searchParams - Inbound query params (from `useSearchParams`).
 * @param routeParams - Dynamic route params (from `useParams`). Only consumed
 *   for the company-specific Microsoft SSO route.
 */
export const buildCoacheeWebUrl = (
    pathname: string,
    searchParams: URLSearchParams,
    routeParams: DeepLinkRouteParams = {},
): string => {
    let url: string | undefined

    // The company-SSO route is dynamic (companyId + ssoId from useParams);
    // compose it from the env base. Trim + require non-empty after trimming so
    // " " / "\t" can't build a URL with empty segments. encodeURIComponent is
    // the right primitive for path segments.
    const companyId = routeParams.companyId?.trim()
    const ssoId = routeParams.ssoId?.trim()
    if (COMPANY_SSO_PATHNAME_PATTERN.test(pathname) && companyId && ssoId) {
        const base = import.meta.env.VITE_APP_COACHEE_WEB_COMPANY_SSO_URL
        if (base) {
            url = `${base}/${encodeURIComponent(companyId)}/${encodeURIComponent(ssoId)}`
        }
    } else {
        url = coacheeWebUrlMap[pathname]
    }

    // Magic-token landings always forward to coachee-web's magic-login route.
    if (searchParams.has('magicToken')) {
        url = import.meta.env.VITE_APP_COACHEE_WEB_MAGIC_LOGIN_URL
    }

    if (!url) return ''

    const urlObject = new URL(url)
    for (const key of ALLOWED_PARAMS) {
        const value = searchParams.get(key)
        if (value !== null) {
            urlObject.searchParams.set(key, value)
        }
    }
    return urlObject.toString()
}

// Append a control param. Empty input (unmapped route) passes straight through
// so callers keep their own fallback rather than choking on `new URL('')`.
const withControlParam = (url: string, key: string, value: string): string => {
    if (!url) return ''
    const urlObject = new URL(url)
    urlObject.searchParams.set(key, value)
    return urlObject.toString()
}

/**
 * "Open in the Sama app" target — the https Universal (iOS) / App (Android)
 * Link on coachee.sama.io. `app_attempt=1` marks a handoff attempt so that a
 * bounce-back (app absent, or handoff blocked by MAM / an in-app browser) is
 * detectable at the chooser. Deliberately carries NO `do_login`, so the OS
 * captures the link into the app — the AASA excludes only `do_login`.
 */
export const buildAppLinkUrl = (
    pathname: string,
    searchParams: URLSearchParams,
    routeParams: DeepLinkRouteParams = {},
): string =>
    withControlParam(
        buildCoacheeWebUrl(pathname, searchParams, routeParams),
        'app_attempt',
        '1',
    )

/**
 * "Continue in browser" target. `do_login=true` both keeps the link in the
 * browser (the AASA excludes it, so the OS won't capture it into the app) and
 * tells coachee-web to consume the token in-page instead of bouncing back.
 */
export const buildContinueOnWebUrl = (
    pathname: string,
    searchParams: URLSearchParams,
    routeParams: DeepLinkRouteParams = {},
): string =>
    withControlParam(
        buildCoacheeWebUrl(pathname, searchParams, routeParams),
        'do_login',
        'true',
    )
